// =============================================================================
// components/KpiRules/KpiRuleList.jsx
//
// Lista plana de regras, agrupada por tecnologia (com contador), no estilo
// de um painel de administração simples: sem cartões nem cores de fundo por
// linha — só um ponto de estado, o nome e destaque leve na linha selecionada.
// O botão "Nova Regra" fica fixo no fundo do painel.
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import {
	Box, Typography, IconButton, Tooltip,
	TextField, InputAdornment, Skeleton, Stack, Button, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import RuleFolderIcon from '@mui/icons-material/FactCheck';
import { useLookups } from '../../hooks/useKpi';

function ListSkeleton() {
	return (
		<Stack spacing={0.75} sx={{ px: 1.5 }}>
			{[0, 1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} variant="rounded" height={30} />
			))}
		</Stack>
	);
}

// ----------------------------------------------------------------------------
// Linha de uma regra — plana, sem moldura própria
// ----------------------------------------------------------------------------
function RuleRow({ rule, isSelected, onSelect, severityMap }) {
	const sev = severityMap[rule.severity];

	return (
		<Box
			onClick={() => onSelect(rule)}
			sx={{
				display: 'flex', alignItems: 'center', gap: 1,
				px: 1.5, py: 0.75, cursor: 'pointer',
				backgroundColor: isSelected ? 'action.selected' : 'transparent',
				'&:hover': { backgroundColor: isSelected ? 'action.selected' : 'action.hover' },
			}}
		>
			<Box sx={{
				width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
				bgcolor: rule.active ? 'success.main' : 'grey.400',
			}} />

			<Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: isSelected ? 600 : 400 }}>
				{rule.kpiName}
			</Typography>

			{sev && (
				<Box sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, bgcolor: sev.colorHex }} />
			)}

			<IconButton size="small" onClick={(e) => { e.stopPropagation(); onSelect(rule); }} sx={{ opacity: isSelected ? 1 : 0.35 }}>
				<EditIcon sx={{ fontSize: 15 }} />
			</IconButton>
		</Box>
	);
}

// ----------------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------------
export default function KpiRuleList({ rulesByTech, totalCount, loading, selectedId, onSelect, onCreate }) {
	const { severityMap, loaded, load } = useLookups();
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (!loaded) load();
	}, [loaded, load]);

	const filteredByTech = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return rulesByTech;

		const out = {};
		Object.keys(rulesByTech).forEach((tech) => {
			const matches = rulesByTech[tech].filter((r) => r.kpiName.toLowerCase().includes(q));
			if (matches.length) out[tech] = matches;
		});
		return out;
	}, [rulesByTech, query]);

	const techs = Object.keys(filteredByTech).sort();

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box sx={{ p: 1.5, pb: 1 }}>
				<TextField
					size="small"
					fullWidth
					placeholder="Pesquisar regras..."
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" color="disabled" />
							</InputAdornment>
						),
					}}
				/>
			</Box>

			<Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
				{loading && <ListSkeleton />}

				{!loading && techs.length === 0 && (
					<Box sx={{ p: 3, textAlign: 'center' }}>
						<RuleFolderIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 1 }} />
						<Typography variant="body2" color="text.secondary">
							{totalCount === 0 ? 'Nenhuma regra configurada.' : 'Nenhuma regra corresponde à pesquisa.'}
						</Typography>
					</Box>
				)}

				{!loading && techs.map((tech) => (
					<Box key={tech} sx={{ mb: 0.5 }}>
						<Box sx={{
							display: 'flex', alignItems: 'center', justifyContent: 'space-between',
							px: 1.5, py: 0.5,
						}}>
							<Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
								{tech}
							</Typography>
							<Box sx={{
								fontSize: 11, fontWeight: 600, color: 'text.secondary',
								bgcolor: 'action.hover', borderRadius: 10, px: 0.9, minWidth: 20, textAlign: 'center',
							}}>
								{filteredByTech[tech].length}
							</Box>
						</Box>
						{filteredByTech[tech].map((rule) => (
							<RuleRow
								key={rule.kpiId}
								rule={rule}
								isSelected={selectedId === rule.kpiId}
								onSelect={onSelect}
								severityMap={severityMap}
							/>
						))}
					</Box>
				))}
			</Box>

			<Divider />
			<Box sx={{ p: 1.5 }}>
				<Button fullWidth variant="text" startIcon={<AddIcon fontSize="small" />} onClick={onCreate}>
					Nova Regra
				</Button>
			</Box>
		</Box>
	);
}
